import React from 'react';
import {
  createBrowserRouter,
  generatePath as generatePathRR,
} from 'react-router';
import { type Empty, loadable } from '@owl-frontend/components';
import { CombineObjectType, enumSet } from '@owl-lib/type-util';
import AppProvider from '../../context/AppProvider';
import type { GeneratePathParam } from '../../helpers/route-types';
import AdminPanelContainer from '../AdminPanelContainer/AdminPanelContainer';
import UsersContainer from '../AdminPanelContainer/UsersContainer/UsersContainer';
import {
  ClaimOverviewShellContainer,
  ClaimOverviewShellLandingContainer,
  ClaimTemplateContainer,
  OwlShellContainer,
  OwlShellInboxContainer,
  ShellRouteDefs,
} from '../ClaimOverviewShellContainer/ClaimOverviewLandingContainer';
import ClaimsContainer from '../ClaimsContainer/ClaimsContainer';
import MainContainer from '../MainContainer/MainContainer';
import OwlVisionLandingContainer from '../OwlVisionLandingContainer/OwlVisionLandingContainer';
import StatusContainer from '../StatusContainer/StatusContainer';
import DefaultRedirect from './DefaultRedirect';

const ClaimOverviewContainer = loadable(
  () => import('../ClaimOverviewContainer/ClaimOverviewContainer')
);

export enum RouteDefs {
  Root = '/',
  Broken = '/uh-oh',
  Main = '/main',
  Admin = '/main/admin',
  AdminUsers = '/main/admin/users',
  Claims = '/main/claims',
  ClaimOverview = '/main/claims/:dossierId',
  Status = '/main/status',
}

export const routeSet = enumSet(RouteDefs);

type GeneratePathRest<Params> = Empty extends Params
  ? [params?: Params]
  : [params: Params];
type TypedGeneratePath = <R extends string>(
  path: R,
  ...rest: GeneratePathRest<CombineObjectType<GeneratePathParam<R>>>
) => string;
export const generatePath = generatePathRR as TypedGeneratePath;

const router = createBrowserRouter([
  {
    path: RouteDefs.Root,
    ErrorBoundary: OwlVisionLandingContainer,
    Component: AppProvider,
    children: [
      {
        index: true,
        element: <DefaultRedirect path={RouteDefs.Claims} />,
      },
      {
        path: RouteDefs.Broken,
        element: <OwlVisionLandingContainer />,
      },
      {
        path: RouteDefs.Main,
        element: <MainContainer />,
        children: [
          {
            index: true,
            element: <DefaultRedirect path={RouteDefs.Claims} />,
          },
          {
            path: RouteDefs.Admin,
            element: <AdminPanelContainer />,
            children: [
              {
                path: RouteDefs.AdminUsers,
                element: <UsersContainer />,
              },
            ],
          },
          {
            path: RouteDefs.Claims,
            element: <ClaimsContainer />,
          },
          {
            path: RouteDefs.Status,
            element: <StatusContainer />,
          },
          {
            path: RouteDefs.ClaimOverview,
            element: <ClaimOverviewContainer />,
          },
        ],
      },
    ],
  },
  // MARK: Owl Shell
  // ===============
  {
    path: ShellRouteDefs.OwlShell,
    ErrorBoundary: OwlVisionLandingContainer,
    Component: AppProvider,
    children: [
      {
        index: true,
        element: <DefaultRedirect path={ShellRouteDefs.Inbox} />,
      },
      {
        path: ShellRouteDefs.Broken,
        element: <OwlVisionLandingContainer />,
      },
      {
        path: ShellRouteDefs.OwlShell,
        element: <OwlShellContainer />,
        children: [
          {
            index: true,
            element: <DefaultRedirect path={ShellRouteDefs.Inbox} />,
          },
          {
            path: ShellRouteDefs.Inbox,
            element: <OwlShellInboxContainer />,
          },
          {
            path: ShellRouteDefs.Claims,
            element: <ClaimsContainer />,
          },
          {
            path: ShellRouteDefs.ClaimOverviewShell,
            element: <ClaimOverviewShellContainer />,
            children: [
              {
                index: true,
                element: <ClaimOverviewShellLandingContainer />,
              },
              {
                path: ShellRouteDefs.ClaimOverviewShellTemplate,
                element: <ClaimTemplateContainer />,
              },
            ],
          },
        ],
      },
    ],
  },
]);

export default router;
